/**
 * Product List Pro - Swiper Slider + Ajax Add to Cart
 */

class PLPSlider extends HTMLElement {
  constructor() {
    super();
    this.init();

    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) {
        this.init();
      }
    });
  }

  init() {
    if (this.dataset.displayMode !== 'grid') {
      this.initSwiper();
    }
    this.initProductCards();
  }

  initSwiper() {
    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl || typeof window.Swiper === 'undefined') return;

    const paginationStyle = swiperEl.dataset.paginationStyle;
    const isTrackStyle = paginationStyle === 'track';

    this.slider = new window.Swiper(swiperEl, {
      speed: this.speed,
      autoplay: this.autoplay,
      navigation: this.navigation,
      pagination: isTrackStyle ? false : this.pagination,
      breakpoints: this.breakpoints,
      rewind: true,
      on: isTrackStyle ? {
        init: () => this.updateTrackBar(),
        slideChange: () => this.updateTrackBar(),
        resize: () => this.updateTrackBar()
      } : {}
    });

    // 轨道点击跳转
    if (isTrackStyle) {
      const track = this.querySelector('.plp-track');
      track?.addEventListener('click', (e) => {
        const rect = track.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        const totalSlides = this.slider.slides.length;
        const slidesPerView = this.slider.params.slidesPerView;
        const maxIndex = Math.ceil(totalSlides - slidesPerView);
        const targetIndex = Math.round(percentage * maxIndex);
        this.slider.slideTo(targetIndex);
      });
    }
  }

  updateTrackBar() {
    const trackBar = this.querySelector('.plp-track__bar');
    if (!trackBar || !this.slider) return;

    const totalSlides = this.slider.slides.length;
    const slidesPerView = this.slider.params.slidesPerView;
    const currentIndex = this.slider.activeIndex;
    const maxIndex = Math.max(1, totalSlides - slidesPerView);
    
    const barWidth = (slidesPerView / totalSlides) * 100;
    const barPosition = (currentIndex / maxIndex) * (100 - barWidth);
    
    trackBar.style.width = barWidth + '%';
    trackBar.style.transform = `translateX(${(barPosition / barWidth) * 100}%)`;
  }

  initProductCards() {
    this.querySelectorAll('.plp-product-card').forEach(card => {
      new PLPProductCard(card);
    });
  }

  get speed() {
    return Number(this.dataset.sliderSpeed) || 300;
  }

  get autoplay() {
    const val = this.dataset.sliderAutoplay;
    return val === '0' || !val ? false : { delay: Number(val) * 1000 };
  }

  get navigation() {
    return {
      enabled: this.dataset.sliderNavigation === 'true',
      prevEl: this.querySelector('.swiper-button-prev'),
      nextEl: this.querySelector('.swiper-button-next')
    };
  }

  get pagination() {
    return {
      enabled: this.dataset.sliderPagination === 'true',
      el: this.querySelector('.swiper-pagination'),
      type: 'bullets',
      dynamicBullets: true,
      dynamicMainBullets: 2
    };
  }

  get breakpoints() {
    const mobileSlides = Number(this.dataset.breakpointMobile) || 1;
    const isOverlayLayout = this.dataset.cardLayout === 'overlay';
    const mobileSlidesPerView = (isOverlayLayout && mobileSlides === 2) ? 1.5 : mobileSlides;
    
    return {
      0: { slidesPerView: mobileSlidesPerView },
      600: { slidesPerView: Number(this.dataset.breakpointTablet) || 2 },
      1200: { slidesPerView: Number(this.dataset.breakpointDesktop) || 4 }
    };
  }
}

/**
 * Product Card - 处理变体选择和加购
 */
class PLPProductCard {
  constructor(el) {
    this.el = el;
    this.variantSelect = el.querySelector('[data-variant-select]');
    this.variantThumbs = el.querySelectorAll('[data-variant-thumb]');
    this.atcBtn = el.querySelector('[data-atc-btn]');
    this.priceWrapper = el.querySelector('[data-price-wrapper]');
    this.mainImage = el.querySelector('.plp-product-card__img:not(.plp-product-card__img--hover)');
    
    this.bindEvents();
  }

  bindEvents() {
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', (e) => this.onVariantChange(e));
    }
    if (this.variantThumbs.length) {
      this.variantThumbs.forEach(thumb => {
        thumb.addEventListener('click', (e) => this.onThumbClick(e, thumb));
      });
    }
    if (this.atcBtn) {
      this.atcBtn.addEventListener('click', (e) => this.onAddToCart(e));
    }
  }

  onThumbClick(e, thumb) {
    e.preventDefault();
    
    const variantId = thumb.dataset.variantId;
    const available = thumb.dataset.variantAvailable === 'true';
    const price = parseInt(thumb.dataset.variantPrice);
    const comparePrice = parseInt(thumb.dataset.variantComparePrice);
    const imageUrl = thumb.dataset.variantImage;
    
    // 检查是否点击的是已选中的缩略图
    const isAlreadyActive = thumb.classList.contains('is-active');
    
    if (isAlreadyActive) {
      // 如果点击的是已选中的缩略图，在默认主图和多属性图之间切换
      if (this.mainImage) {
        const defaultImageUrl = this.el.querySelector('.plp-product-card__img')?.getAttribute('data-default-src');
        const currentSrc = this.mainImage.src;
        
        // 判断当前显示的是否是默认主图
        const isShowingDefault = currentSrc.includes(defaultImageUrl?.split('?')[0]);
        
        if (isShowingDefault) {
          // 当前是默认主图，切换到多属性图
          this.mainImage.src = imageUrl;
          this.mainImage.srcset = imageUrl;
        } else {
          // 当前是多属性图，切换回默认主图
          this.mainImage.src = defaultImageUrl;
          this.mainImage.srcset = defaultImageUrl;
        }
      }
      
      // 保持当前选中状态不变
      return;
    }
    
    // 点击的是未选中的缩略图，正常切换
    this.variantThumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    
    if (this.mainImage && imageUrl) {
      this.mainImage.src = imageUrl;
      this.mainImage.srcset = imageUrl;
    }

    if (this.atcBtn) {
      this.atcBtn.dataset.variantId = variantId;
      this.atcBtn.disabled = !available;
    }

    this.updatePrice(price, comparePrice);
  }

  onVariantChange(e) {
    const select = e.target;
    const option = select.options[select.selectedIndex];
    const variantId = option.value;
    const available = option.dataset.available === 'true';
    const price = parseInt(option.dataset.price);
    const comparePrice = parseInt(option.dataset.comparePrice);

    this.atcBtn.dataset.variantId = variantId;
    this.atcBtn.disabled = !available;
    
    const textEl = this.atcBtn.querySelector('[data-atc-text]');
    if (textEl) {
      textEl.textContent = available 
        ? (window.plpStrings?.addToCart || 'Add to cart')
        : (window.plpStrings?.soldOut || 'Sold out');
    }

    this.updatePrice(price, comparePrice);
  }

  updatePrice(price, comparePrice) {
    if (!this.priceWrapper) return;

    const formatMoney = (cents) => {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      return '$' + (cents / 100).toFixed(2);
    };

    const isOverlay = this.el.classList.contains('plp-product-card--overlay');
    const isInfoBelow = this.el.classList.contains('plp-product-card--info-below');
    let html = '';

    if (comparePrice && comparePrice > price) {
      if (isOverlay && !isInfoBelow) {
        // 图片内遮罩层模式 - 保持白色样式
        html = '<s data-compare-price>' + formatMoney(comparePrice) + '</s>' +
               '<span data-price>' + formatMoney(price) + '</span>';
      } else if (isOverlay && isInfoBelow) {
        // 图片下方模式
        html = '<s data-compare-price>' + formatMoney(comparePrice) + '</s>' +
               '<span data-price>' + formatMoney(price) + '</span>';
      } else {
        // 默认布局
        html = '<s class="plp-product-card__price-compare" data-compare-price>' + formatMoney(comparePrice) + '</s>' +
               '<span class="plp-product-card__price-sale" data-price>' + formatMoney(price) + '</span>';
      }
    } else {
      html = '<span data-price>' + formatMoney(price) + '</span>';
      if (!isOverlay) {
        html = '<span class="plp-product-card__price-regular" data-price>' + formatMoney(price) + '</span>';
      }
    }
    this.priceWrapper.innerHTML = html;
  }

  async onAddToCart(e) {
    e.preventDefault();
    
    const btn = this.atcBtn;
    const variantId = btn.dataset.variantId;
    
    if (!variantId || btn.disabled) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
          sections: 'cart-drawer,cart-icon-bubble'
        })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const data = await response.json();

      btn.classList.remove('is-loading');
      btn.classList.add('is-success');

      this.updateCart(data);
      PLPToast.show(data);
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));

      setTimeout(() => {
        btn.classList.remove('is-success');
        btn.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Add to cart error:', error);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  updateCart(data) {
    const sections = data.sections;
    if (!sections) return;

    if (sections['cart-drawer']) {
      const cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const newContent = doc.querySelector('#CartDrawer');
        if (newContent) {
          cartDrawerEl.innerHTML = newContent.innerHTML;
        }
      }
      document.querySelector('.drawer__inner')?.classList.remove('is-empty');
      document.querySelector('cart-drawer')?.classList.remove('is-empty');
    }

    if (sections['cart-icon-bubble']) {
      const cartIconEl = document.getElementById('cart-icon-bubble');
      if (cartIconEl) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newContent = doc.querySelector('.shopify-section');
        if (newContent) {
          cartIconEl.innerHTML = newContent.innerHTML;
        }
      }
    }
  }
}

// Register custom element
if (!customElements.get('plp-slider')) {
  customElements.define('plp-slider', PLPSlider);
}

/**
 * Toast Notification
 */
const PLPToast = {
  container: null,

  init() {
    if (this.container) return;
    
    this.container = document.createElement('div');
    this.container.className = 'plp-toast-container';
    this.container.innerHTML = '<div class="plp-toast">' +
      '<div class="plp-toast__icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg></div>' +
      '<div class="plp-toast__content"><div class="plp-toast__title"></div><div class="plp-toast__message"></div></div>' +
      '<button class="plp-toast__view" type="button">View Cart</button>' +
      '<button class="plp-toast__close" type="button" aria-label="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>' +
      '</div>';
    document.body.appendChild(this.container);

    this.container.querySelector('.plp-toast__view').addEventListener('click', () => {
      this.hide();
      this.openCartDrawer();
    });

    this.container.querySelector('.plp-toast__close').addEventListener('click', () => {
      this.hide();
    });
  },

  show(data) {
    this.init();
    const title = data.product_title || data.title || 'Item';
    const variant = data.variant_title || '';
    
    this.container.querySelector('.plp-toast__title').textContent = 'Added to cart';
    this.container.querySelector('.plp-toast__message').textContent = variant ? title + ' - ' + variant : title;
    this.container.classList.add('is-visible');

    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => this.hide(), 4000);
  },

  hide() {
    if (this.container) {
      this.container.classList.remove('is-visible');
    }
    clearTimeout(this.hideTimer);
  },

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }
    const cartIcon = document.querySelector('[data-cart-drawer-toggle], .header__icon--cart, #cart-icon-bubble a');
    if (cartIcon) {
      cartIcon.click();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:open'));
  }
};

/**
 * Facets.js Compatibility Layer
 * Ensures multiple PLP sections with filtering don't conflict
 */
document.addEventListener('DOMContentLoaded', () => {
  // Check if this is a PLP section with filtering enabled
  const plpFacetsSections = document.querySelectorAll('.plp-facets-section');
  
  if (plpFacetsSections.length === 0) return;
  
  // If there's only one section with filtering, we're good
  if (plpFacetsSections.length === 1) return;
  
  // If multiple sections with filtering exist, warn in console
  console.warn('Product List Pro: Multiple sections with filtering detected. Only one section with filtering should be used per page to avoid conflicts.');
});
